import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import type { ActionPlan, Notified } from '../../../api/workEnvironmentTypes';
import {
  useApproveActionPlanMutation,
  useRemindAboutPlanReviewMutation,
  useWithdrawPlanReviewMutation,
} from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import { ApprovalNotice, isApproved, noticeText, ReviewMissing } from '../planView';
import { notifiedText, reminderStatus } from '../reminderView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = {
  surveyId: number;
  plan: ActionPlan;
  notice: ApprovalNotice;
  reviewMissing: ReviewMissing;
  /** When the others hear that the plan was sent or approved, right after the user did it */
  notified: Notified;
  onBack: () => void;
  /** Only for those who make the plans, while the plan can still be changed */
  onEdit?: () => void;
};

const COLORS: Record<ApprovalNotice, 'info' | 'warning' | 'success'> = {
  askedToApprove: 'info',
  awaitingApproval: 'info',
  approvedWithoutSafetyRep: 'warning',
  approved: 'success',
};

/** Where the plan is with the safety representative, and what can be done next */
export const PlanStatusCard = ({
  surveyId,
  plan,
  notice,
  reviewMissing,
  notified,
  onBack,
  onEdit,
}: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  const approve = useApproveActionPlanMutation(surveyId);
  const remind = useRemindAboutPlanReviewMutation(surveyId);
  const withdraw = useWithdrawPlanReviewMutation(surveyId);
  const failed = [approve, withdraw].some((mutation) => mutation.isError);
  const reminder = reminderStatus(remind, t, i18n.language);
  const sent = notifiedText(notified, t, i18n.language);

  return (
    <section aria-labelledby="plan-status-heading" className={process.card}>
      <div className={process.cardBody}>
        <Alert data-color={COLORS[notice]}>
          <Heading level={2} data-size="2xs" id="plan-status-heading">
            {t(`workEnvironmentPlan.approval.${notice}.title`)}
          </Heading>
          <Paragraph data-size="sm">{noticeText(notice, plan, reviewMissing, t, i18n.language)}</Paragraph>
          {notice === 'approvedWithoutSafetyRep' && plan.approved?.deviation && (
            <Paragraph data-size="sm">
              {t('workEnvironmentPlan.approval.reason', { reason: plan.approved.deviation })}
            </Paragraph>
          )}
          {sent && (
            <Paragraph data-size="sm" role="status">
              {sent}
            </Paragraph>
          )}
        </Alert>
        {failed && (
          <Alert data-color="danger">
            <Paragraph>{t('workEnvironmentPlan.approval.failed')}</Paragraph>
          </Alert>
        )}
        {reminder.failed && (
          <Alert data-color="danger">
            <Paragraph>{reminder.failed}</Paragraph>
          </Alert>
        )}
        <div className={classes.planActions}>
          {notice === 'askedToApprove' && may('reviewPlan') && (
            <Button
              variant="primary"
              data-size="sm"
              loading={approve.isPending}
              onClick={() => approve.mutate({ planId: plan.id, deviation: null })}
            >
              {t('workEnvironmentPlan.approval.approve')}
            </Button>
          )}
          {notice === 'awaitingApproval' &&
            onEdit &&
            (plan.canRemind ? (
              <Button
                variant="secondary"
                data-size="sm"
                loading={remind.isPending}
                onClick={() => remind.mutate(plan.id)}
              >
                {t('workEnvironmentPlan.approval.remind')}
              </Button>
            ) : (
              <Paragraph data-size="sm" className={process.subtle} role="status">
                {reminder.sent ?? t('workEnvironmentPlan.approval.reminded')}
              </Paragraph>
            ))}
          {notice === 'awaitingApproval' && onEdit && (
            <Button
              variant="secondary"
              data-size="sm"
              loading={withdraw.isPending}
              onClick={() => withdraw.mutate(plan.id, { onSuccess: onEdit })}
            >
              {t('workEnvironmentPlan.approval.withdraw')}
            </Button>
          )}
          {isApproved(notice) && onEdit && (
            <Button variant="secondary" data-size="sm" onClick={onEdit}>
              {t('workEnvironmentPlan.summary.edit')}
            </Button>
          )}
          <Button variant="tertiary" data-size="sm" onClick={onBack}>
            {t(onEdit ? 'workEnvironmentPlan.approval.otherArea' : 'workEnvironmentPlan.approval.back')}
          </Button>
        </div>
      </div>
    </section>
  );
};
